import React, { useCallback, useEffect, useRef, useState } from "react";
import { Banner } from "@shopify/polaris";
import { connect } from "react-redux";
import bulkActionApi from "../../../services/bulkActionApi";
import {
  patchSettingsFields,
  refreshSettings,
} from "../../../redux/actions/settingsActions";

const POLL_INTERVAL_MS = 5000;

export const parseCsvDownloadLink = (links) => {
  if (!links) {
    return null;
  }

  let link = links;
  if (Array.isArray(link)) {
    link = link[0];
  } else if (typeof link === "object") {
    link = Object.values(link)[0];
  }

  if (typeof link !== "string") {
    return null;
  }

  return link.replace(/^["']|["']$/g, "").trim();
};

export const isCsvExportReady = (data) =>
  Boolean(parseCsvDownloadLink(data?.links));

/** Case 1: server returns immediately with a background pending job. */
export const isCsvExportStartedInBackground = (data) => {
  if (!data || data.success === false) {
    return false;
  }
  if (parseCsvDownloadLink(data?.links)) {
    return false;
  }
  return data.status === "pending";
};

export const openCsvDownloadLink = (link) => {
  const anchor = document.createElement("a");
  anchor.href = link;
  anchor.rel = "noopener noreferrer";
  anchor.target = "_blank";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

export const completeCsvExportDownload = async ({
  link,
  patchSettingsFields,
  refreshSettings,
}) => {
  openCsvDownloadLink(link);
  await bulkActionApi.acknowledgeCsvDownloaded();
  if (typeof patchSettingsFields === "function") {
    patchSettingsFields({ isCsvExportJobRunning: false });
  }
  if (typeof refreshSettings === "function") {
    await refreshSettings();
  }
};

const CsvDownloadJobBanner = ({
  isCsvExportJobRunning = false,
  patchSettingsFields,
  refreshSettings,
}) => {
  const [statusData, setStatusData] = useState(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [pollError, setPollError] = useState("");
  const pollTimerRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(() => {
    return bulkActionApi
      .getCreateCsvStatus()
      .then((res) => {
        const data = res?.data ?? res;
        setStatusData(data);
        setPollError("");

        if (parseCsvDownloadLink(data?.links)) {
          stopPolling();
          return "completed";
        }
        if (data?.status === "failed" || data?.failedAt) {
          stopPolling();
          return "failed";
        }
        return "pending";
      })
      .catch(() => {
        setPollError("Unable to check CSV export status. Please try again shortly.");
        return "error";
      });
  }, [stopPolling]);

  useEffect(() => {
    if (!isCsvExportJobRunning) {
      setStatusData(null);
      setPollError("");
      stopPolling();
      return undefined;
    }

    let cancelled = false;

    const poll = () => {
      fetchStatus().then(() => {
        if (cancelled) {
          stopPolling();
        }
      });
    };

    poll();
    pollTimerRef.current = window.setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [isCsvExportJobRunning, fetchStatus, stopPolling]);

  const handleDownload = useCallback(async () => {
    const link = parseCsvDownloadLink(statusData?.links);
    if (!link) {
      setPollError("Download link is not available yet. Please wait a moment.");
      return;
    }

    setIsAcknowledging(true);
    setPollError("");

    try {
      await completeCsvExportDownload({
        link,
        patchSettingsFields,
        refreshSettings,
      });
      setStatusData(null);
    } catch {
      setPollError("Failed to confirm download. Please try again.");
    } finally {
      setIsAcknowledging(false);
    }
  }, [statusData, patchSettingsFields, refreshSettings]);

  if (!isCsvExportJobRunning) {
    return null;
  }

  const progress = statusData?.progress;
  const processedProducts = Number(progress?.processedProducts) || 0;
  const totalProducts = Number(progress?.totalProducts) || 0;
  const downloadLink = parseCsvDownloadLink(statusData?.links);
  const isCompleted = Boolean(downloadLink);
  const isFailed =
    statusData?.status === "failed" || Boolean(statusData?.failedAt);

  if (isFailed) {
    return (
      <div className="csv-download-job-banner marketContactUsBanner">
        <Banner status="critical" title="CSV export failed">
          <p>
            {statusData?.message ||
              "We could not generate your pricing CSV. Please try downloading again."}
          </p>
        </Banner>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="csv-download-job-banner marketContactUsBanner">
        <Banner
          status="success"
          title="CSV export ready"
          action={{
            content: isAcknowledging ? "Downloading..." : "Download CSV",
            onAction: handleDownload,
            loading: isAcknowledging,
            disabled: isAcknowledging,
          }}
        >
          <p>
            Your pricing CSV is ready. Click <strong>Download CSV</strong> to get
            your file.
          </p>
          {pollError ? <p>{pollError}</p> : null}
        </Banner>
      </div>
    );
  }

  return (
    <div className="csv-download-job-banner marketContactUsBanner">
      <Banner status="info" title="CSV export in progress">
        {/* <p>
          We are generating your pricing CSV file. This may take a few minutes.
          {totalProducts > 0 ? (
            <>
              {" "}
              Progress: {processedProducts.toLocaleString()} of{" "}
              {totalProducts.toLocaleString()} products.
            </>
          ) : null}
        </p> */}
         <p>
          We are generating your pricing CSV file. This may take a few minutes.
        </p>
        {pollError ? <p>{pollError}</p> : null}
      </Banner>
    </div>
  );
};

const mapStateToProps = (state) => ({
  isCsvExportJobRunning: Boolean(state.settings.isCsvExportJobRunning),
});

const mapDispatchToProps = {
  patchSettingsFields,
  refreshSettings,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CsvDownloadJobBanner);
